/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { createReducer } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';

import Turfmaster, { StateMachineValue } from '~/server/src/tmmodel';

import type { SpectatorStatus } from './actions';
import {
  HorseRanks,
  leaveAcked,
  leaveFailed,
  LeaveFailureReason,
  LeaveLifecycleStatus,
  leaveRequested,
  leaveReset,
  leaveRetrying,
  notifyConnectionTimeout,
  resetConnectionTimeout,
  setConnected,
  setCurrentState,
  setHorseDropped,
  setHorseFinished,
  setHorseHandi,
  setHorsePoints,
  setHorseRanks,
  setHorsesBehindGoal,
  setHorsesDropped,
  setHorsesFinished,
  setHorsesHandi,
  setHorsesPoints,
  setHorsesRanks,
  setHorsesStarted,
  setPlayers,
  setRound,
  setSpectatorStatus,
  setTurnDicePlayerId,
  setTurnDices,
  setTurnDicesCur,
  setTurnOrder,
  setTurnPos,
  setTurnType,
  startDebug,
  startFree,
  startTournament,
  startTraining,
} from './actions';

export interface Player {
  id: number;
  userId: string;
  name: string;
  type: number;
  cardType: number;
  totalRank: number;
  playerName: string;
  country: string;
}

export interface GameState {
  // flag to indicate ui if socket of remote controller is connected
  readonly connected: boolean;
  readonly connectionTimeout: boolean;
  readonly spectatorStatus: SpectatorStatus;

  readonly leaveStatus: LeaveLifecycleStatus;
  readonly leaveAttempt: number;
  readonly leaveError?: { reason: LeaveFailureReason; message?: string } | null;

  readonly currentState: StateMachineValue;
  readonly round: number;

  readonly turnpos: number;
  readonly turnorder: number[];
  readonly turndices: number[];
  readonly turndicescur: number;
  readonly turntype: number;
  readonly turndiceplayerid: number;

  readonly players: Player[];

  readonly horsesRanks: HorseRanks[];
  readonly horsesHandi: number[];
  readonly horsesPoints: number[][];
  readonly horsesFinished: boolean[];
  readonly horsesDropped: boolean[];
  readonly horsesStarted: boolean[];
  readonly horsesBehindGoal: boolean[];
}

export const initialGameState: GameState = {
  connected: false,
  connectionTimeout: false,
  spectatorStatus: 'idle',

  leaveStatus: 'idle',
  leaveAttempt: 0,
  leaveError: null,

  currentState: Turfmaster.stateMachine.NEWGAME,
  round: 0,

  turnpos: 0,
  turnorder: [],
  turndices: [],
  turndicescur: 0,
  turntype: Turfmaster.turntypes.CARDS,
  turndiceplayerid: 0,

  players: [],

  horsesRanks: [],
  horsesHandi: [],
  horsesPoints: [],
  horsesFinished: [],
  horsesDropped: [],
  horsesStarted: [],
  horsesBehindGoal: [],
};

export default createReducer(initialGameState, (builder) => {
  builder
    .addCase(setConnected, (state, action) => {
      state.connected = action.payload;
    })
    .addCase(resetConnectionTimeout, (state) => {
      state.connectionTimeout = false;
    })
    .addCase(notifyConnectionTimeout, (state) => {
      state.connectionTimeout = true;
    })
    .addCase(setSpectatorStatus, (state, action) => {
      state.spectatorStatus = action.payload;
    })
    .addCase(startTraining, (_state, _action) => cloneDeep(initialGameState))
    .addCase(startFree, (_state, _action) => cloneDeep(initialGameState))
    .addCase(startTournament, (_state, _action) => cloneDeep(initialGameState))
    .addCase(startDebug, (_state, _action) => cloneDeep(initialGameState))
    .addCase(leaveRequested, (state, action) => {
      state.leaveStatus = 'pending';
      state.leaveAttempt = action.payload.attempt;
      state.leaveError = null;
    })
    .addCase(leaveRetrying, (state, action) => {
      state.leaveStatus = 'pending';
      state.leaveAttempt = action.payload.attempt;
    })
    .addCase(leaveAcked, (state) => {
      state.leaveStatus = 'succeeded';
      state.leaveError = null;
    })
    .addCase(leaveFailed, (state, action) => {
      state.leaveStatus = 'failed';
      state.leaveError = action.payload;
    })
    .addCase(leaveReset, (state) => {
      state.leaveStatus = 'idle';
      state.leaveAttempt = 0;
      state.leaveError = null;
    })
    .addCase(setCurrentState, (state, action) => {
      state.currentState = action.payload;
    })
    .addCase(setRound, (state, action) => {
      state.round = action.payload;
    })
    .addCase(setTurnPos, (state, action) => {
      state.turnpos = action.payload;
    })
    .addCase(setTurnOrder, (state, action) => {
      state.turnorder = action.payload;
    })
    .addCase(setTurnDices, (state, action) => {
      state.turndices = action.payload;
    })
    .addCase(setTurnDicesCur, (state, action) => {
      state.turndicescur = action.payload;
    })
    .addCase(setTurnType, (state, action) => {
      state.turntype = action.payload;
    })
    .addCase(setTurnDicePlayerId, (state, action) => {
      state.turndiceplayerid = action.payload;
    })
    .addCase(setPlayers, (state, action) => {
      state.players = action.payload;
    })
    .addCase(setHorsesRanks, (state, action) => {
      state.horsesRanks = action.payload;
    })
    .addCase(setHorseRanks, (state, action) => {
      state.horsesRanks[action.payload.index] = action.payload.value;
    })
    .addCase(setHorsesHandi, (state, action) => {
      state.horsesHandi = action.payload;
    })
    .addCase(setHorseHandi, (state, action) => {
      state.horsesHandi[action.payload.index] = action.payload.value;
    })
    .addCase(setHorsesPoints, (state, action) => {
      state.horsesPoints = action.payload;
    })
    .addCase(setHorsePoints, (state, action) => {
      state.horsesPoints[action.payload.index] = action.payload.value;
    })
    .addCase(setHorsesFinished, (state, action) => {
      state.horsesFinished = action.payload;
    })
    .addCase(setHorseFinished, (state, action) => {
      state.horsesFinished[action.payload.index] = action.payload.value;
    })
    .addCase(setHorsesDropped, (state, action) => {
      state.horsesDropped = action.payload;
    })
    .addCase(setHorseDropped, (state, action) => {
      state.horsesDropped[action.payload.index] = action.payload.value;
    })
    .addCase(setHorsesStarted, (state, action) => {
      state.horsesStarted = action.payload;
    })
    .addCase(setHorsesBehindGoal, (state, action) => {
      state.horsesBehindGoal = action.payload;
    });
});
